import Vue from 'vue'
import Vuex from 'vuex'
import axios from "axios";
import {errorHandler} from "@/services/errorHandler";
import transactionManager from "@/store/modules/transactionManager";
import TimeModule from "@/store/modules/time-module";
import auth from "@/store/modules/auth";
import locations from "@/store/modules/locations";
import Kazang from "@/store/modules/Kazang";

import VuexPersistence from 'vuex-persist';
import { set, get, del, createStore } from 'idb-keyval';

// Create custom IDB store that handles missing stores gracefully
const customStore = createStore('vuex-persist', 'keyval');

const safeGet = async (key) => {
    try {
        return await get(key, customStore);
    } catch (err) {
        console.warn('IndexedDB get error:', err.message);
        return undefined;
    }
};

const safeSet = async (key, value) => {
    try {
        await set(key, value, customStore);
    } catch (err) {
        console.warn('IndexedDB set error:', err.message);
    }
};

const safeDel = async (key) => {
    try {
        await del(key, customStore);
    } catch (err) {
        console.warn('IndexedDB del error:', err.message);
    }
};

const indexedDBStorage = {
    getItem: safeGet,
    setItem: safeSet,
    removeItem: safeDel
};

Vue.use(Vuex)

function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

const vuexLocal = new VuexPersistence({
    storage: indexedDBStorage, // Use IndexedDB
    asyncStorage: true, // Enable async storage handling
    restoreState: async (key) => {
        try {
            const value = await safeGet(key);
            // console.log('Restoring state from IndexedDB:', { key, value });
            return value || {};
        } catch (err) {
            console.error('Error restoring state:', err);
            return {};
        }
    },
    saveState: async (key, value) => {
        try {
            // console.log('Saving state to IndexedDB:', { key, value });
            await safeSet(key, value);
        } catch (err) {
            console.error('Error saving state:', err);
        }
    }
});

const store = new Vuex.Store({
    state: {
        // apiKey: '5c685245-70eb-4eea-994c-6a333085ec7f',//dump
       // apiKey: '744ddfre-55eedff69-qzzwd2',//live
        loading: false,
    },
    getters: {
        getApiKey: state => state.apiKey,
        getLoadingState: (state) => state.loading,
    },
    mutations: {
        setLoadingState: (state, payload) => state.loading = payload,
    },
    actions: {
        /**
         * Sends an HTTP request through the shared axios transport and owns the
         * idempotency identity ("localid") for the request
         * (gap CG-2, SPEC §4.4 D4 frontend half, §7.1 criteria 1 and 2).
         *
         * WHY THE IDENTITY IS OWNED HERE
         * ------------------------------
         * The Chicken Little API caches every response it produces under the
         * `localid` the client sends: `LocalId.getLocalId(localid)` returns the
         * stored row and `LocalId.getResponse()` replays it verbatim - including
         * the error row that `ErrorHandler.getError(Exception, LocalId)` caches.
         * A `localid` therefore behaves as a cache key for the OUTCOME of a
         * request, so presenting one again re-reads that outcome instead of
         * evaluating the request again.
         *
         * `payload` is owned by the CALLER and is re-used for the next attempt
         * (LoginForm.vue re-dispatches `login` with the same `formData` after a
         * failed attempt), so the identity has to be released when the request
         * settles. Leaving it behind made the corrected attempt re-present the
         * failed attempt's identity, and the backend answered with the cached
         * failure - the customer-visible defect of TicketApp #29691 (SPEC §7.1
         * criterion 1). Releasing it on the failure path as well as on the
         * success path is the frontend half of D4.
         *
         * IDENTITY RULES ENFORCED HERE
         * -----------------------------
         * 1. A NEW REQUEST NEVER SHARES AN IDENTITY WITH ANOTHER REQUEST. The
         *    first attempt honours an identity the caller pinned on the payload
         *    (previous behaviour) and mints a fresh one when the caller pinned
         *    none; every request started by a timeout retry mints a fresh
         *    identity, because the timed-out attempt may have left a cached
         *    outcome under its identity (the cached ERROR of the very attempt
         *    that timed out, against a backend that still caches error
         *    responses) and a retry must never be answered from a previous
         *    outcome (SPEC §7.1 criterion 2).
         * 2. AN IDENTITY IS RELEASED WHEN THE REQUEST SETTLES - on success AND
         *    on failure - so the next attempt made with the same payload starts
         *    from a clean identity.
         *
         * ASSUMPTIONS AND EDGE CASES
         * ---------------------------
         * - The backend keys the in-flight work of a request by its `localid`, so
         *   re-reading an accepted-but-unfinished request re-uses that identity
         *   on purpose: a fresh identity would start a SECOND execution of the
         *   same operation (a duplicate order, a duplicate WhatsApp code)
         *   instead of collecting the outcome of the first one. The re-read
         *   happens once - the previous implementation re-sent the identical
         *   payload up to three times - and if the request is still unfinished
         *   the state is surfaced as a visible error instead of being retried
         *   blindly (SPEC §7.1 criterion 4).
         * - A transport failure on the first attempt may mean the backend never
         *   received the request, so retrying it under a fresh identity can
         *   execute the operation twice. The backend invalidates the earlier
         *   one-time code (task 007) and the login flow expires the previous
         *   code, so the operator ends up with a single usable code.
         * - A transport failure on a RE-READ is not retried: the original
         *   request is still in flight server-side, so issuing another request
         *   could duplicate it.
         *
         * @param {Object} context   Vuex action context (unused - kept in the
         *                            signature for compatibility with every
         *                            existing dispatch).
         * @param {string} url        Absolute endpoint URL.
         * @param {Object} payload   Request body. The `localid` property is
         *                            owned by this action: written before an
         *                            attempt, released when the action settles.
         * @param {Object} [headers] Optional request headers.
         * @param {string} method    HTTP verb passed to axios.
         * @returns {Promise<Object>} Resolves with `data.response` - the
         *                            unwrapped `response` member of the backend
         *                            envelope - exactly as before. Rejects through
         *                            `errorHandler.tomcatError` so the failure
         *                            stays visible to the operator.
         */
        sendRequest(context, {url, payload, headers, method}) {
            // Re-send budget for one dispatch: the initial attempt plus at most
            // three re-sends - the same ceiling the previous implementation
            // used. Every re-send draws from this single budget, so one dispatch
            // can never make more than four requests.
            const maxRetries = 3;
            let retriesUsed = 0;

            // How many times a request the backend has accepted but not yet
            // finished may be re-read before the state is surfaced. The previous
            // implementation re-sent the identical payload up to three times.
            const maxUnprocessedReads = 1;
            let unprocessedReads = 0;

            /**
             * Publishes a brand-new idempotency identity on the caller-owned
             * payload and returns it.
             *
             * Called whenever a NEW request is started - the first attempt when
             * the caller pinned no identity, and every timeout retry - so that
             * no two requests can ever be answered from one another's cached
             * outcome (rule 1 in the JSDoc above). A re-read is NOT a new
             * request and deliberately keeps the in-flight request's identity.
             */
            const mintLocalId = () => {
                payload.localid = generateUUID();
                return payload.localid;
            };

            /**
             * Releases the idempotency identity from the caller-owned payload.
             *
             * Called when the request settles, on success AND on failure, so the
             * next attempt made with the same payload object starts from a clean
             * identity instead of re-reading this attempt's cached outcome
             * (rule 2 in the JSDoc above).
             */
            const releaseLocalId = () => {
                payload.localid = null;
            };

            /**
             * Classifies a failure as a transport-level timeout or abort.
             *
             * Only these are retried. A timeout does NOT mean the request
             * failed - it leaves the outcome UNKNOWN: the attempt may never
             * have reached the backend, or it may have executed server-side
             * with only its response lost. The retry is therefore issued under
             * a FRESH identity (see the re-issue branch below): re-presenting
             * the timed-out attempt's identity would re-read that attempt's
             * cached outcome, which - against a backend that still caches
             * error responses - is the very failure that timed out.
             *
             * A fresh identity means the operation MAY execute twice, because
             * the first attempt may already have executed server-side. That is
             * the accepted, bounded trade-off of retrying after a timeout -
             * the same trade-off stated in the ASSUMPTIONS section of this
             * action's JSDoc - and NOT a guarantee that a re-issue is free of
             * side effects. A backend 4xx/5xx is a real answer and is never
             * retried.
             *
             * @param {*} err The rejection value.
             * @returns {boolean} True when the failure is a transport-level
             *                   timeout, so the request may be re-issued under a
             *                   fresh identity.
             */
            const isTimeoutFailure = (err) => {
                if (!err) {
                    return false;
                }
                if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
                    return true;
                }
                return typeof err.message === 'string' && err.message.includes('timeout');
            };

            // The first attempt honours an identity the caller pinned on the
            // payload (the behaviour of the previous implementation) and mints a
            // new one when there is none.
            if (!payload.localid) {
                mintLocalId();
            }

            /**
             * Performs one HTTP attempt against the current payload.
             *
             * @param {boolean} isReRead True when this attempt re-reads a request
             *                            the backend has already accepted and has
             *                            not finished yet. A re-read keeps the
             *                            identity of that in-flight request,
             *                            because the operation is keyed by it and
             *                            a new identity would start a second
             *                            operation (a duplicate order, a duplicate
             *                            OTP) instead of asking about the first.
             * @returns {Promise<Object>} Resolves with `data.response`.
             */
            const attemptRequest = (isReRead) => {
                return axios[method](url, payload, {
                    headers: headers || {}
                }).then(({data}) => {
                    if (!data.processed) {
                        // The backend accepted the request but has not finished it
                        // yet.
                        if (unprocessedReads < maxUnprocessedReads && retriesUsed < maxRetries) {
                            unprocessedReads++;
                            retriesUsed++;
                            console.log(`Attempt ${retriesUsed}: request accepted but not processed yet, re-reading request ${payload.localid}.`);
                            return attemptRequest(true);
                        }
                        // Out of re-reads: report the state honestly instead of
                        // re-sending blindly or resolving with an unfinished
                        // answer (SPEC §7.1 criterion 4).
                        const notProcessed = new Error('The server accepted the request but has not completed it yet. Please try again.');
                        notProcessed.code = 'ERR_REQUEST_NOT_PROCESSED';
                        throw notProcessed;
                    }
                    // The public contract of this action: resolve with the
                    // unwrapped `response` member of the backend envelope, the
                    // same value every caller already receives.
                    return data.response;
                }, (err) => {
                    if (isReRead) {
                        // ANY failure while re-reading an accepted-but-unfinished
                        // request is surfaced as it is - not only a timeout. The
                        // operation may still complete on the backend, so do not
                        // start a second one: the failure is handed to the
                        // caller instead.
                        throw err;
                    }
                    if (isTimeoutFailure(err) && retriesUsed < maxRetries) {
                        retriesUsed++;
                        // The attempt timed out, so the backend may never have
                        // received it. The re-send is treated as a NEW request and
                        // is given a NEW identity: re-using the timed-out
                        // attempt's identity would re-read that attempt's cached
                        // outcome - against a backend that still caches error
                        // responses, that cached outcome is the ERROR of the
                        // timed-out attempt (gap CG-2).
                        mintLocalId();
                        console.log(`Attempt ${retriesUsed}: request timed out, retrying as a new request with id ${payload.localid}.`);
                        return attemptRequest(false);
                    }
                    throw err; // Re-throw the error if it's not a timeout or retries are exhausted
                });
            };

            return attemptRequest(false)
                .then(response => {
                    // Success: release the identity so the caller can re-use this
                    // payload for a new request without replaying this one.
                    releaseLocalId();
                    return response
                })
                .catch(err => {
                    // Failure: release the identity as well. Leaving it behind on
                    // the caller-owned payload would make the caller's next
                    // attempt re-read this attempt's cached outcome - the
                    // customer-visible defect this change removes (gap CG-2 /
                    // SPEC §7.1 criterion 1).
                    releaseLocalId();
                    return errorHandler.tomcatError(err);
                });
        },
        getCompanyLogo(context, payload) {

        }
    },
    modules: {
        transactionManager,TimeModule,auth,locations,Kazang
    },
    plugins: [
        vuexLocal.plugin,
    ],
})
export default store;

